import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { CATALOG_TABS } from '@/Pages/tabRoutes';
import type { CatalogItem, Category, Station } from './lib';
import ItemsTab from './ItemsTab';
import StationsTab from './StationsTab';
import CategoryTree from './CategoryTree';
import MenusTab from './MenusTab';
import ModifiersTab from './ModifiersTab';
import MealsTab from './MealsTab';
import PackagesTab from './PackagesTab';
import { Button } from '@/components/ui/button';

/**
 * P3-10 — catalog management (§6). Every tab talks straight to its own module:
 * `/catalog`, `/categories`, `/menus`, `/modifiers`, `/meals`, `/packages`. No
 * Redux slice; the API is the state.
 *
 * Items and categories are **one** view rather than two tabs. Editing a
 * category meant leaving the products behind, coming back and re-finding the
 * filter; the tree navigates and the list works, and the selected category is
 * owned here so both halves stay on the same answer.
 *
 * Area price lists used to be a tab here. The feature is gone — the venue
 * priced everything off `basePrice` and never attached a list to an area, so it
 * was a screen that could only ever produce data nothing read.
 */
export default function Catalog() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/catalog', CATALOG_TABS);
  /** `''` is "all products". Lives here because the tree and the list share it. */
  const [categoryId, setCategoryId] = useState('');

  // Reference data several tabs need: the item list feeds menus, meals and
  // packages; categories and stations feed the item filters.
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [itemsError, setItemsError] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesError, setCategoriesError] = useState(false);
  const [stations, setStations] = useState<Station[]>([]);

  const loadReference = useCallback(() => {
    api
      .get<{ data: CatalogItem[] }>('/catalog', { params: { limit: 200 } })
      .then(({ data }) => {
        setItems(data.data ?? []);
        setItemsError(false);
      })
      // Unlike the pickers below and elsewhere in this app (`Billing/Register`'s
      // `useOpenChecks`, `Billing/Checks`'s branch filter), there is no
      // narrower permission this could legitimately be missing: reaching this
      // screen at all already required `menu.view` (`Routes.tsx`), and `GET
      // /catalog` asks for nothing more. So an empty result here is never a
      // permission story — it is either a tenant with a genuinely empty
      // catalogue or a broken request, and under RLS an unbound tenant read
      // answers with zero rows rather than an error. Swallowing this to a bare
      // `setItems([])` told a manager building a menu "you have no items" when
      // the read had actually failed — said out loud instead, the same
      // treatment `Register.tsx`'s `useOpenBill` gives its own bill content
      // for the identical reason.
      .catch(() => {
        setItems([]);
        setItemsError(true);
      });
    api
      .get<{ data: Category[] }>('/categories', { params: { limit: 200 } })
      .then(({ data }) => {
        setCategories(data.data ?? []);
        setCategoriesError(false);
      })
      // Same story as `/catalog` above — `GET /categories` is gated on the
      // same `menu.view` this route already requires, so there is no
      // legitimate permission explanation for an empty result here either.
      .catch(() => {
        setCategories([]);
        setCategoriesError(true);
      });
    // Stations need `kitchen.view`; without it the station filter is simply empty.
    api
      .get<Station[]>('/kitchen', { params: { limit: 100 } })
      .then(({ data }) => setStations(Array.isArray(data) ? data : []))
      .catch(() => setStations([]));
  }, []);

  useEffect(loadReference, [loadReference]);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('nav.catalog')}</h1>
        <p className="text-sm text-ink-muted">{t('catalog.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((entry) => ({ key: entry.id, label: t(entry.labelKey) }))}
        value={current}
        onChange={select}
        label={t('nav.catalog')}
        idPrefix="catalog"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {/* The item picker's own failure, named rather than swallowed — see
          `loadReference` above. Shown only on the tabs it actually feeds, so a
          manager on Items or Stations (which do not read `items`) is never
          told about a fetch that has nothing to do with what they are
          looking at. */}
      {itemsError && (current === 'menus' || current === 'meals' || current === 'packages') && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
        >
          <span>{t('catalog.itemsUnavailable')} — {t('catalog.itemsUnavailableHint')}</span>
          <Button size="sm" variant="outline" onClick={loadReference}>
            {t('common.tryAgain')}
          </Button>
        </div>
      )}
      {categoriesError && current === 'items' && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
        >
          <span>{t('catalog.categoriesUnavailable')} — {t('catalog.categoriesUnavailableHint')}</span>
          <Button size="sm" variant="outline" onClick={loadReference}>
            {t('common.tryAgain')}
          </Button>
        </div>
      )}

      {current === 'items' && (
        // `min-w-0` on the list column is what lets the item table scroll
        // inside itself instead of pushing the tree off a tablet screen.
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <CategoryTree
            className="lg:w-80 lg:shrink-0"
            selectedId={categoryId}
            onSelect={setCategoryId}
            onChanged={loadReference}
          />
          <div className="min-w-0 flex-1">
            <ItemsTab
              categories={categories}
              stations={stations}
              onChanged={loadReference}
              categoryId={categoryId}
              onCategoryChange={setCategoryId}
            />
          </div>
        </div>
      )}
      {current === 'menus' && <MenusTab items={items} />}
      {current === 'modifiers' && <ModifiersTab />}
      {current === 'meals' && <MealsTab items={items} />}
      {current === 'packages' && <PackagesTab items={items} />}
      {current === 'stations' && <StationsTab onChanged={loadReference} />}
    </div>
  );
}
